import { useTranslation } from 'react-i18next'
import Tippy from '@tippyjs/react'

import Icon from '../../ui/Icon'

import { Booking, BookingFunding } from '../../../types/booking/booking'
import { PaymentMethod } from '../../../types/payment/payment'
import { formatMoney, formatPoints } from '../../../utils/booking/booking'

const FUNDING_BADGES: Record<BookingFunding, string> = {
  [BookingFunding.PASS]: 'badge-accent',
  [BookingFunding.MIXED]: 'badge-accent',
  [BookingFunding.PAYMENT]: 'badge-info',
  [BookingFunding.OFFLINE]: 'badge-success',
  [BookingFunding.FREE]: 'badge-secondary',
}

export const useBookingFundingLabel = () => {
  const { t } = useTranslation(['history', 'bookings'])

  return (booking: Booking, withPrice = true) => {
    const method =
      booking.funding === BookingFunding.OFFLINE && booking.paymentMethod
        ? t(
            `bookings:booking.all-booking-list.payment-method.${booking.paymentMethod.toLowerCase()}`
          )
        : undefined
    const label =
      method ?? t(`history.funding.${booking.funding.toLowerCase()}`)
    const price =
      withPrice &&
      booking.funding !== BookingFunding.PASS &&
      booking.price &&
      booking.price.amount > 0
        ? ` · ${formatMoney(booking.price)}`
        : ''
    return `${label}${price}`
  }
}

export const BookingFundingBadge = ({ booking }: { booking: Booking }) => {
  const fundingLabel = useBookingFundingLabel()

  return (
    <span className={`badge ${FUNDING_BADGES[booking.funding]}`}>
      {fundingLabel(booking)}
    </span>
  )
}

const FUNDING_ICONS: Record<BookingFunding, string[]> = {
  [BookingFunding.PASS]: ['crown'],
  [BookingFunding.MIXED]: ['crown', 'card'],
  [BookingFunding.PAYMENT]: ['card'],
  [BookingFunding.OFFLINE]: ['cash'],
  [BookingFunding.FREE]: ['sparkle'],
}

const OFFLINE_ICONS: Partial<Record<PaymentMethod, string>> = {
  [PaymentMethod.CASH]: 'cash',
  [PaymentMethod.BANK_TRANSFER]: 'bank',
}

/** How a booking was funded, as a muted icon with the method in a tooltip (compact alternative to the badge). */
export const BookingFundingIcon = ({ booking }: { booking: Booking }) => {
  const { t } = useTranslation(['history', 'bookings'])
  const offline =
    booking.funding === BookingFunding.OFFLINE && booking.paymentMethod
  const label = offline
    ? t(
        `bookings:booking.all-booking-list.payment-method.${booking.paymentMethod!.toLowerCase()}`
      )
    : t(`history.funding.${booking.funding.toLowerCase()}`)
  const icons = offline
    ? [OFFLINE_ICONS[booking.paymentMethod!] ?? 'cash']
    : FUNDING_ICONS[booking.funding]

  return (
    <Tippy
      content={label}
      placement="right"
      className="ph-tooltip"
      delay={[1000, 0]}
    >
      <span
        className="funding-icon muted d-inline-flex align-items-center gap-1"
        role="img"
        aria-label={label}
        tabIndex={0}
      >
        {icons.map(icon => (
          <Icon key={icon} name={icon} size={16} />
        ))}
      </span>
    </Tippy>
  )
}

/** What the booking cost: money paid, prepaid minutes used, or both. */
export const BookingAmount = ({ booking }: { booking: Booking }) => {
  const { t } = useTranslation('history')
  const parts = [
    ...(booking.prepaidMinutes && booking.prepaidMinutes > 0
      ? [t('history.amount.minutes', { count: booking.prepaidMinutes })]
      : []),
    ...(booking.funding !== BookingFunding.PASS &&
    booking.price &&
    booking.price.amount > 0
      ? [formatMoney(booking.price)]
      : []),
  ]
  const points = booking.pointsAmount ?? 0
  return (
    <span className="mono text-nowrap">
      {parts.length > 0 ? parts.join(' + ') : '—'}
      {points > 0 && (
        <span className="muted tiny d-block">
          {t('history.amount.points-included', { points: formatPoints(points) })}
        </span>
      )}
    </span>
  )
}

